import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { config } from "../config";
import { Apple, Globe, Smartphone, X, ChevronRight } from "lucide-react";
import { GlassCard } from "./ui/glass-card";

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DownloadModal: React.FC<DownloadModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const openUrl = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  const optionClass =
    "flex items-center gap-4 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-left transition-all hover:bg-white/10 active:scale-[0.98]";

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-modal-title"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/75 backdrop-blur-md"
        aria-label="Chiudi"
        onClick={onClose}
      />

      <GlassCard
        hover={false}
        variant="strong"
        className="download-sheet relative z-10 w-full max-w-md rounded-t-2xl sm:rounded-2xl border-white/15 shadow-2xl p-0 overflow-hidden flex flex-col max-h-[min(85dvh,90svh)] mb-0"
      >
        <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-white/25 flex-shrink-0 sm:hidden" />

        <div className="flex items-center justify-between gap-3 px-5 pt-3 pb-3 border-b border-white/10 flex-shrink-0">
          <div>
            <h2
              id="download-modal-title"
              className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-200 to-teal-500"
            >
              Crea la tua Ecard
            </h2>
            <p className="text-sm text-white/60 mt-0.5">Scegli come iniziare</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* min-h-0 is required so the flex child can shrink and scroll on short viewports */}
        <div
          className="flex flex-col gap-3 p-5 overflow-y-auto overscroll-contain min-h-0 flex-1"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <button
            type="button"
            onClick={() => openUrl(config.appStoreUrl)}
            className={optionClass}
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
              <Apple className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white">Scarica su App Store</p>
              <p className="text-xs text-white/55">iPhone e iPad</p>
            </div>
            <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/40" />
          </button>

          <button
            type="button"
            onClick={() => openUrl(config.playStoreUrl)}
            className={optionClass}
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white">Scarica su Google Play</p>
              <p className="text-xs text-white/55">Android</p>
            </div>
            <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/40" />
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              window.location.href = "/register";
            }}
            className={optionClass}
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
              <Globe className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white">Registrati dal web</p>
              <p className="text-xs text-white/55">
                Crea subito la tua card nel browser
              </p>
            </div>
            <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/40" />
          </button>
        </div>
      </GlassCard>
    </div>,
    document.body,
  );
};

export default DownloadModal;
